import {ApolloQueryResult, gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications} from 'audit/DataModificationsModal';
import BenchlingConfig from 'BenchlingConfig';
import {
  ActionOrigin,
  StudyPhase,
  StudyType,
  ReviewStatus,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ToastMessage from 'nav/toasts/ToastMessage';
import ReviewConstants from 'notebook/reviews/ReviewConstants';
import notifications from 'notifications';
import {getPowAgnosticPublishedFlowchartVersionId} from 'procedures/utils/powAgnosticFlowchartHelpers';
import * as TrialSampleTestingStudyDesignDAO from 'studies/network/TrialSampleTestingStudyDesignDAO';
import ConfirmCancelModal from 'studies/sidepanel/ConfirmCancelModal';
import {isProceedtoExecutionBlocked} from 'studies/StudyPhaseUtils';
import TableOfContents from 'studies/typed/shared/TableOfContents';
import {
  isDesignMissingRequiredApprovalForStudyExecution,
  IsDesignMissingRequiredApprovalStudyNodeFragment,
} from 'studies/typed/utils/StudyDesignUtils';
import {StudyDesignEditorHeaderButtons} from 'studies/workspace/design/StudyDesignEditorHeaderButtons';
import {ErrorBannerWithIndicator} from 'studies/workspace/design/StudyDesignPrintView';
import {FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {WHITE, GRAY_LIGHT} from 'ui-library/Colors';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import legacyRunModal from 'ui-library/legacyRunModal';
import * as net from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import {StudyDesignTabContentsProps} from '../utils/TypedStudyTypes';
import {ClinicalStudyDesignTabContentsQuery} from './__generated/ClinicalStudyDesignTabContentsQuery';
import ClinicalStudyDatePicker, {useDateValidation} from './ClinicalStudyDatePicker';
import SampleDefinitionTable, {SampleDefinition} from './SampleDefinitionTable';
import {SampleTestPlan, SampleTestPlanTable} from './SampleTestPlanTable';
import {TreatmentGroup, TreatmentGroupTable} from './TreatmentGroupTable';

const CONTENTS = ['Dates', 'Treatment group', 'Planned samples', 'Sample testing plan'];

/**
 * Message to show when the design cannot be edited due to study phase.
 *
 * @package exported for testing
 */
export const STUDY_PHASE_LOCKED_EDITS_REASON =
  'This study design can only be edited while the study is in the design phase.';

export const CLINICAL_STUDY_DESIGN_TAB_CONTENTS_QUERY: DocumentNode = gql`
  query ClinicalStudyDesignTabContentsQuery($studyId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        studySchema {
          ... on StudySchemaNode {
            id
            studyType
            isDesignApprovalRequired
          }
        }
        phase
        studyDesign {
          ... on HasPermissionsInterface {
            permissions {
              writable
            }
          }
          ... on ReviewableInterface {
            id
            review {
              id
              status
            }
          }
          ... on TrialSampleTestingStudyDesignNode {
            id
            modifiedAt
            startDate
            endDate
            treatmentGroups {
              ... on TreatmentGroupNode {
                id
                name
                label
                description
                targetNumberOfSubjects
                isReadyForExecution
              }
            }
            sampleDefinitions {
              ... on SampleDefinitionNode {
                id
                label
                visit
                allowsNewSubjects
                isReadyForExecution
                sampleType {
                  ... on SampleTypeNode {
                    id
                    label
                  }
                }
                treatmentGroup {
                  ... on TreatmentGroupNode {
                    id
                    label
                  }
                }
              }
            }
            sampleTestPlans {
              ... on StudySampleTestPlanNode {
                id
                sampleDefinition {
                  ... on SampleDefinitionNode {
                    id
                    label
                  }
                }
                procedure {
                  ... on ProcedureNode {
                    id
                    procedureFlowchart {
                      ... on ProcedureFlowchartNode {
                        id
                        publishedFlowchartVersion {
                          ... on ProcedureFlowchartVersionNode {
                            id
                          }
                        }
                      }
                    }
                    publishedProcedureFlowchartConfigVersionProxy {
                      ... on ProcedureFlowchartConfigVersionProxyNode {
                        id
                      }
                    }
                  }
                  ...InaccessibleInfoGraphQL
                }
                isReadyForExecution
              }
            }
          }
        }
        permissions {
          writable
        }
      }
      ...IsDesignMissingRequiredApprovalStudyNodeFragment
      ...InaccessibleInfoGraphQL
    }
    allSampleTypes {
      ... on SampleTypeNode {
        id
        label
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${IsDesignMissingRequiredApprovalStudyNodeFragment.fragment}
`;

/**
 * Returns user-facing text explaining why study design editing is disabled based on the review status
 * provided. Only provides reasons for locked review statuses; other reasons the design may be locked such as
 * the current user's permissions are not covered by this method.
 *
 * @package exported for testing.
 */
export function getReviewStatusLockedReason(status: ReviewStatus): string | null {
  if (!ReviewConstants.LOCKED_STATUSES.includes(status)) {
    return null;
  }
  // This text is based on `getEditWarning` in `ReviewUtil`
  if (status === ReviewStatus.REVIEW_SNAPSHOT_IN_PROGRESS) {
    return 'Editing is disabled because this study design is currently being snapshotted for review.';
  } else if (status === ReviewStatus.ACTION_REQUIRED || status === ReviewStatus.NEEDS_REVIEW) {
    return 'Editing is disabled because this study design is currently under review.';
  } else if (status === ReviewStatus.ACCEPTANCE_SNAPSHOT_IN_PROGRESS) {
    return 'Editing is disabled because this study design is currently being snapshotted after acceptance.';
  } else if (status === ReviewStatus.ACCEPTED) {
    return 'Editing is disabled because this study design has already been accepted.';
  } else {
    // We can use `assertNever` on this status when the TODO in `ReviewConstants.ts` to use the ReviewStatus
    // enum is complete, to ensure completeness here. For now, this is checked through unit tests.
    throw new Error(`Unexpected review status: ${status}`);
  }
}

/**
 * Returns user-facing text explaining why the study design is locked, if the reason is one of the following:
 * 1. The study is in a phase that does not allow editing the design
 * 2. The design is in a locked review status
 *
 * If both apply, the study phase reason is returned.
 *
 * @package exported for testing.
 */
export function getLockedReason(study: StudyNode): string | undefined {
  if (study.studyDesign == null) {
    return undefined;
  }

  let reviewLockedReason: string | null = null;
  if (
    BenchlingConfig.STUDIES_ENABLE_REVIEW &&
    study.studyDesign.review &&
    ReviewConstants.LOCKED_STATUSES.includes(study.studyDesign.review.status)
  ) {
    reviewLockedReason = getReviewStatusLockedReason(study.studyDesign.review.status);
  }

  if (study.phase !== StudyPhase.DESIGN) {
    return STUDY_PHASE_LOCKED_EDITS_REASON;
  } else if (reviewLockedReason != null) {
    return reviewLockedReason;
  } else {
    return undefined;
  }
}

type StudyNode = ExtractByTypename<ClinicalStudyDesignTabContentsQuery['study'], 'StudyNode'>;
type SampleTypeNode = ExtractByTypename<
  ClinicalStudyDesignTabContentsQuery['allSampleTypes'][number],
  'SampleTypeNode'
>;
type TrialSampleTestingStudyDesignNode = NonNullable<StudyNode['studyDesign']>;

function QueryingClinicalStudyDesignTabContents({
  studyId,
  viewer,
  locked,
  allowReturnToDesignPhase,
}: StudyDesignTabContentsProps): JSX.Element | null {
  const queryResult = useBenchlingQuery<ClinicalStudyDesignTabContentsQuery>(
    CLINICAL_STUDY_DESIGN_TAB_CONTENTS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: true,
      variables: {studyId},
    }
  );
  if (queryResult.type === QueryResultType.PENDING) {
    return <HorizontalThrobber />;
  }
  if (queryResult.type === QueryResultType.ERROR) {
    return (
      <ErrorBannerWithIndicator reason="query-error" queryResultType={queryResult.type}>
        Failed to load the study design.
      </ErrorBannerWithIndicator>
    );
  }
  if (isInaccessible(queryResult.data.study)) {
    return (
      <ErrorBannerWithIndicator reason="inaccessible" queryResultType={queryResult.type}>
        The study is inaccessible.
      </ErrorBannerWithIndicator>
    );
  }

  const study = queryResult.data.study;

  if (study.studyDesign == null) {
    return (
      <ErrorBannerWithIndicator reason="no-study-design" queryResultType={queryResult.type}>
        The study design could not be displayed.
      </ErrorBannerWithIndicator>
    );
  }

  const studyDesign = study.studyDesign;
  assert(
    studyDesign.__typename === 'TrialSampleTestingStudyDesignNode',
    `Expected study to have a TrialSampleTestingStudyDesignNode, not ${studyDesign.__typename}`,
    {studyId}
  );

  return (
    <ClinicalStudyDesignTabContents
      study={study}
      studyDesign={studyDesign}
      allSampleTypes={queryResult.data.allSampleTypes}
      viewer={viewer}
      locked={
        locked ||
        !study.permissions.writable ||
        !study.studyDesign.permissions.writable ||
        study.phase !== StudyPhase.DESIGN
      }
      lockedReason={getLockedReason(study)}
      refetch={queryResult.refetch}
      key={`${studyDesign.modifiedAt}-${study.phase}`}
      isDesignApprovalRequiredToProceed={isDesignMissingRequiredApprovalForStudyExecution(study)}
      allowReturnToDesignPhase={allowReturnToDesignPhase ?? false}
    />
  );
}

// exported for testing
export async function updateTrialSampleTestingStudyDesignWithDataMods(
  studyDesignId: string,
  previousModifiedAt: string,
  updateTrialSampleTestingStudyDesignParams: Partial<TrialSampleTestingStudyDesignDAO.UpdateTrialSampleTestingStudyDesignParams>,
  isReadyForExecution: boolean,
  refetch: () => Promise<ApolloQueryResult<ClinicalStudyDesignTabContentsQuery>>
): Promise<void> {
  const notificationId = `study-design-save:${studyDesignId}`;
  try {
    if (isReadyForExecution) {
      await callAsyncWithDataModifications({
        changeSummary: 'The study design will be updated.',
        onPerformAction: async (dataModsHeader?: DataModificationsHeader) => {
          await TrialSampleTestingStudyDesignDAO.updateTrialSampleTestingStudyDesign(
            studyDesignId,
            previousModifiedAt,
            updateTrialSampleTestingStudyDesignParams,
            dataModsHeader
          );
          await refetch();
          notifications.success('Study design was successfully saved.', {
            id: notificationId,
          });
        },
        apiIds: [studyDesignId],
        analyticsParams: {
          module: 'STUDIES',
          actionOrigin: ActionOrigin.STUDY_DESIGN_PAGE,
          modifiedAction: 'STUDY_DESIGN_UPDATE',
          numModifiedTotalItems: 1,
          entryId: null,
        },
        useProjectLevelDataModsLogic: true,
      });
    } else {
      await withLoadingScreen(
        (async () => {
          await TrialSampleTestingStudyDesignDAO.updateTrialSampleTestingStudyDesign(
            studyDesignId,
            previousModifiedAt,
            updateTrialSampleTestingStudyDesignParams
          );
          await refetch();
        })(),
        'Saving study design...'
      );
      notifications.success('Study design was successfully saved.', {
        id: notificationId,
      });
    }
  } catch (e) {
    if (net.isServerRequestError(e)) {
      notifications.error(
        <ToastMessage
          header="Failed to save study design"
          body={notifications.getFriendlyErrorAsElement(e, '')}
        />,
        {id: notificationId}
      );
    } else {
      notifications.close(notificationId);
      throw e;
    }
  }
}

export function isStudyDesignEmpty(studyDesign: TrialSampleTestingStudyDesignNode): boolean {
  return (
    studyDesign.treatmentGroups.length === 0 &&
    studyDesign.sampleDefinitions.length === 0 &&
    studyDesign.sampleTestPlans.length === 0
  );
}

export function ClinicalStudyDesignTabContents({
  study,
  studyDesign,
  allSampleTypes,
  viewer,
  locked,
  lockedReason,
  allowReturnToDesignPhase,
  isDesignApprovalRequiredToProceed,
  refetch,
}: {
  study: StudyNode;
  studyDesign: TrialSampleTestingStudyDesignNode;
  allSampleTypes: Array<SampleTypeNode>;
  viewer: boolean;
  locked: boolean;
  lockedReason?: string;
  allowReturnToDesignPhase: boolean;
  isDesignApprovalRequiredToProceed: boolean;
  refetch: () => Promise<ApolloQueryResult<ClinicalStudyDesignTabContentsQuery>>;
}): JSX.Element {
  const sampleTypeLabelToIds = useMemo(
    () => new Map(allSampleTypes.map((sampleType) => [sampleType.label, sampleType.id])),
    [allSampleTypes]
  );
  const startDate = studyDesign.startDate ?? '';
  const endDate = studyDesign.endDate ?? '';
  const resolvedTreatmentGroups = studyDesign.treatmentGroups;
  const resolvedSampleDefinitions = studyDesign.sampleDefinitions;
  const resolvedSampleTestPlans = studyDesign.sampleTestPlans;
  const existingTreatmentGroups: Array<TreatmentGroup> = useMemo(
    () =>
      resolvedTreatmentGroups.length > 0
        ? resolvedTreatmentGroups.map((treatmentGroup) => ({
            id: treatmentGroup.id,
            name: treatmentGroup.name,
            label: treatmentGroup.label,
            description: treatmentGroup.description,
            targetNumberOfSubjects: treatmentGroup.targetNumberOfSubjects,
            validationState: {
              isMissingName: false,
              isDuplicateName: false,
              isNameLengthOverLimit: false,
              isMissingLabel: false,
              isDuplicateLabel: false,
              isLabelLengthOverLimit: false,
              isDescLengthOverLimit: false,
              isTargetNumberOfSubjectsUnderMin: false,
              isTargetNumberOfSubjectsNotAnInt: false,
            },
            isReadyForExecution: treatmentGroup.isReadyForExecution,
          }))
        : [
            {
              name: '',
              label: '',
              description: '',
              targetNumberOfSubjects: 0,
              validationState: {
                isMissingName: true,
                isDuplicateName: false,
                isNameLengthOverLimit: false,
                isMissingLabel: true,
                isDuplicateLabel: false,
                isLabelLengthOverLimit: false,
                isDescLengthOverLimit: false,
                isTargetNumberOfSubjectsUnderMin: false,
                isTargetNumberOfSubjectsNotAnInt: false,
              },
              isReadyForExecution: false,
            },
          ],
    [resolvedTreatmentGroups]
  );
  const existingSampleDefinitions: Array<SampleDefinition> = useMemo(
    () =>
      resolvedSampleDefinitions.length > 0
        ? resolvedSampleDefinitions.map((sampleDefinition) => ({
            id: sampleDefinition.id,
            label: sampleDefinition.label,
            visit: sampleDefinition.visit,
            sampleTypeLabel: sampleDefinition.sampleType.label,
            treatmentGroupLabel: sampleDefinition.treatmentGroup.label,
            allowsNewSubjects: sampleDefinition.allowsNewSubjects,
            validationState: {
              isMissingLabel: false,
              isLabelLengthOverLimit: false,
              isDuplicateLabel: false,
              isMissingVisit: false,
              isVisitLengthOverLimit: false,
              isMissingSampleTypeLabel: false,
              isSampleTypeLabelOutsideAvailableOptions: false,
              isMissingTreatmentGroupLabel: false,
              isTreatmentGroupLabelOutsideAvailableOptions: false,
              isDuplicateSampleMatchingCriteria: false,
              isMissingNewSubjectsPerTreatmentGroup: false,
            },
            isReadyForExecution: sampleDefinition.isReadyForExecution,
          }))
        : [
            {
              label: '',
              visit: '',
              sampleTypeLabel: '',
              treatmentGroupLabel: '',
              allowsNewSubjects: false,
              validationState: {
                isMissingLabel: true,
                isLabelLengthOverLimit: false,
                isDuplicateLabel: false,
                isMissingVisit: true,
                isVisitLengthOverLimit: false,
                isMissingSampleTypeLabel: true,
                isSampleTypeLabelOutsideAvailableOptions: false,
                isMissingTreatmentGroupLabel: true,
                isTreatmentGroupLabelOutsideAvailableOptions: false,
                isDuplicateSampleMatchingCriteria: false,
                isMissingNewSubjectsPerTreatmentGroup: false,
              },
              isReadyForExecution: false,
            },
          ],
    [resolvedSampleDefinitions]
  );
  const existingSampleTestPlans: Array<SampleTestPlan> = useMemo(
    () =>
      resolvedSampleTestPlans.length > 0
        ? resolvedSampleTestPlans.map((sampleTestPlan) => ({
            id: sampleTestPlan.id,
            sampleDefinition: {
              label: sampleTestPlan.sampleDefinition.label,
            },
            assayId: isInaccessible(sampleTestPlan.procedure)
              ? sampleTestPlan.procedure.invalidId
              : sampleTestPlan.procedure.id,
            publishedAssayVersionId: !isInaccessible(sampleTestPlan.procedure)
              ? getPowAgnosticPublishedFlowchartVersionId(sampleTestPlan.procedure)
              : null,
            isReadyForExecution: sampleTestPlan.isReadyForExecution,
            rowIndex: null,
            validationState: {
              isMissingSampleDefinition: false,
              isMissingAssay: false,
              isInvalidSampleDefinition: false,
              isDuplicateSampleDefinitionRow: false,
              isDuplicateTestPlan: false,
            },
          }))
        : [
            {
              sampleDefinition: null,
              assayId: null,
              publishedAssayVersionId: null,
              isReadyForExecution: false,
              rowIndex: null,
              validationState: {
                isMissingSampleDefinition: true,
                isMissingAssay: true,
                isInvalidSampleDefinition: false,
                isDuplicateSampleDefinitionRow: false,
                isDuplicateTestPlan: false,
              },
            },
          ],
    [resolvedSampleTestPlans]
  );

  // ensures that the ref is set before rendering the TOC
  // ToC uses UseDocumentScrollState which requires the ref to be set
  // to check if elements are in the in view of the container
  const childRef = useRef<HTMLDivElement>(null);

  // saves draft versions of the treatment groups, sample definitions, and sample test plans
  // in case the user cancels the edit
  const [isRefSet, setIsRefSet] = useState(false);
  const [draftStudyStartDate, setDraftStudyStartDate] = useState<string>(startDate);
  const [draftStudyEndDate, setDraftStudyEndDate] = useState<string>(endDate);
  const [draftTreatmentGroups, setDraftTreatmentGroups] =
    useState<Array<TreatmentGroup>>(existingTreatmentGroups);
  const [draftSampleDefinitions, setDraftSampleDefinitions] =
    useState<Array<SampleDefinition>>(existingSampleDefinitions);
  const [draftSampleTestPlans, setDraftSampleTestPlans] =
    useState<Array<SampleTestPlan>>(existingSampleTestPlans);

  const initialEditingState = isStudyDesignEmpty(studyDesign) && !locked;
  const [isEditingStudyDesign, setStudyDesignEditingState] = useState(initialEditingState);

  const isTreatmentGroupStateInvalid = useMemo(
    () =>
      draftTreatmentGroups.some(
        (treatmentGroup) =>
          treatmentGroup.validationState == null ||
          Object.values(treatmentGroup.validationState).some((v) => v)
      ),
    [draftTreatmentGroups]
  );

  const isSampleDefinitionStateInvalid = useMemo(
    () =>
      draftSampleDefinitions.some(
        (sampleDefinition) =>
          sampleDefinition.validationState == null ||
          Object.values(sampleDefinition.validationState).some((v) => v) ||
          sampleTypeLabelToIds.get(sampleDefinition.sampleTypeLabel) === undefined
      ),
    [draftSampleDefinitions, sampleTypeLabelToIds]
  );

  const isSampleTestPlanStateInvalid = useMemo(
    () =>
      draftSampleTestPlans.some(
        (sampleTestPlan) =>
          sampleTestPlan.validationState == null ||
          Object.values(sampleTestPlan.validationState).some((v) => v)
      ),
    [draftSampleTestPlans]
  );

  const dateValidation = useDateValidation({
    studyStartDate: draftStudyStartDate,
    studyEndDate: draftStudyEndDate,
  });

  const isDisabled = useMemo(
    () =>
      isTreatmentGroupStateInvalid ||
      isSampleDefinitionStateInvalid ||
      isSampleTestPlanStateInvalid ||
      !dateValidation.isValid,
    [
      isTreatmentGroupStateInvalid,
      isSampleDefinitionStateInvalid,
      isSampleTestPlanStateInvalid,
      dateValidation,
    ]
  );

  useEffect(() => {
    if (childRef.current) {
      setIsRefSet(true);
    }
  }, []);
  const contents = CONTENTS.map((content, index) => ({
    title: content,
    id: `designTab_${index}`,
  }));

  const isReadyForExecution = useMemo(
    () =>
      draftTreatmentGroups.some((treatmentGroup) => treatmentGroup.isReadyForExecution) ||
      draftSampleDefinitions.some((sampleDefinition) => sampleDefinition.isReadyForExecution) ||
      draftSampleTestPlans.some((sampleTestPlan) => sampleTestPlan.isReadyForExecution),
    [draftTreatmentGroups, draftSampleDefinitions, draftSampleTestPlans]
  );

  const handleSave = useCallback(async () => {
    await updateTrialSampleTestingStudyDesignWithDataMods(
      studyDesign.id,
      studyDesign.modifiedAt,
      {
        startDate: draftStudyStartDate || null,
        endDate: draftStudyEndDate || null,
        treatmentGroups: draftTreatmentGroups.map((treatmentGroup) => ({
          id: treatmentGroup.id ?? null,
          name: treatmentGroup.name,
          label: treatmentGroup.label,
          description: treatmentGroup.description,
          targetNumberOfSubjects: treatmentGroup.targetNumberOfSubjects,
        })),
        sampleDefinitions: draftSampleDefinitions.map((sampleDefinition) => {
          const sampleTypeId = sampleTypeLabelToIds.get(sampleDefinition.sampleTypeLabel)!;
          assert(sampleTypeId !== undefined, 'Expected sampleTypeId to be defined after validation');
          return {
            id: sampleDefinition.id ?? null,
            label: sampleDefinition.label,
            visit: sampleDefinition.visit,
            allowsNewSubjects: sampleDefinition.allowsNewSubjects,
            treatmentGroupLabel: sampleDefinition.treatmentGroupLabel,
            sampleTypeId,
          };
        }),
        sampleTestPlans: draftSampleTestPlans.map((sampleTestPlan) => {
          assert(sampleTestPlan.assayId != null, 'Expected assayId to be defined after validation');
          return {
            id: sampleTestPlan.id ?? null,
            sampleDefinitionLabel: sampleTestPlan.sampleDefinition!.label, // validated in isSampleTestPlanStateInvalid
            procedureId: sampleTestPlan.assayId, // validated in isSampleTestPlanStateInvalid
          };
        }),
      },
      isReadyForExecution,
      refetch
    );
  }, [
    draftTreatmentGroups,
    draftSampleDefinitions,
    draftSampleTestPlans,
    draftStudyStartDate,
    draftStudyEndDate,
    isReadyForExecution,
    refetch,
    sampleTypeLabelToIds,
    studyDesign.id,
    studyDesign.modifiedAt,
  ]);

  const handleCancel = useCallback(async (): Promise<void> => {
    const confirmCancel = await legacyRunModal(ConfirmCancelModal, {
      userFacingContextName: 'study design',
    });
    if (confirmCancel == null) {
      return;
    }
    setDraftStudyStartDate(startDate);
    setDraftStudyEndDate(endDate);
    setDraftTreatmentGroups(existingTreatmentGroups);
    setDraftSampleDefinitions(existingSampleDefinitions);
    setDraftSampleTestPlans(existingSampleTestPlans);
    setStudyDesignEditingState(false);
  }, [startDate, endDate, existingTreatmentGroups, existingSampleDefinitions, existingSampleTestPlans]);

  const handleEdit = useCallback((): void => {
    setStudyDesignEditingState(true);
  }, []);

  const getTitle = (studyType: StudyType): string => {
    switch (studyType) {
      case StudyType.CLINICAL:
        return 'Clinical Study Design';
      case StudyType.PRECLINICAL:
        return 'Preclinical Study Design';
      default:
        assert(false, `Unexpected study type: ${studyType}`);
    }
  };

  return (
    <div
      className="study-design-export--contents-and-toc-container"
      {...getTestProps({component: 'StudyDesignTab', element: 'root'})}
    >
      {isRefSet && childRef.current && !viewer && (
        <div className={css(styles.toc)}>
          <TableOfContents title="CONTENTS" contents={contents} container={childRef.current} />
        </div>
      )}
      <div className="study-design-export--contents-container" ref={childRef}>
        <div className="study-design-export--design-tab-header">
          <div>
            <div className="study-design-export--design-tab-header-title">
              {getTitle(study.studySchema.studyType)}
            </div>
            <div className="study-design-export--design-tab-header-description">
              Set up treatment groups, planned samples, and a sample testing plan
            </div>
          </div>
          {!viewer && (
            <div className={css(styles.headerButtons)}>
              <StudyDesignEditorHeaderButtons
                studyId={study.id}
                studyDesign={studyDesign}
                studyPhase={study.phase}
                studyType={study.studySchema.studyType}
                refetch={async () => {
                  await refetch();
                }}
                locked={locked}
                lockedReason={lockedReason}
                handleSave={handleSave}
                handleCancel={handleCancel}
                handleEdit={handleEdit}
                isEditingStudyDesign={isEditingStudyDesign}
                isSaveDisabled={isDisabled}
                isProceedtoExecutionBlocked={isProceedtoExecutionBlocked(studyDesign, study.phase)}
                isDesignApprovalRequiredToProceed={isDesignApprovalRequiredToProceed}
                allowReturnToDesignPhase={allowReturnToDesignPhase}
              />
            </div>
          )}
        </div>
        <div>
          <div className="study-design-export--content-div" key={0} id={`designTab_${0}`}>
            <ClinicalStudyDatePicker
              studyStartDate={draftStudyStartDate}
              studyEndDate={draftStudyEndDate}
              setStudyStartDate={setDraftStudyStartDate}
              setStudyEndDate={setDraftStudyEndDate}
              validation={dateValidation}
              locked={!isEditingStudyDesign}
            />
          </div>
          <div className="study-design-export--content-div" key={1} id={`designTab_${1}`}>
            <TreatmentGroupTable
              tableTitle={contents[1].title}
              treatmentGroups={draftTreatmentGroups}
              setTreatmentGroups={setDraftTreatmentGroups}
              locked={!isEditingStudyDesign}
              viewer={viewer}
            />
          </div>
          <div className="study-design-export--content-div" key={2} id={`designTab_${2}`}>
            <SampleDefinitionTable
              tableTitle={contents[2].title}
              sampleDefinitions={draftSampleDefinitions}
              setSampleDefinitions={setDraftSampleDefinitions}
              treatmentGroups={draftTreatmentGroups}
              locked={!isEditingStudyDesign}
              viewer={viewer}
            />
          </div>
          <div
            className="study-design-export--content-div study-design-export--content-last-div"
            key={3}
            id={`designTab_${3}`}
          >
            <SampleTestPlanTable
              tableTitle={contents[3].title}
              sampleTestPlans={draftSampleTestPlans}
              sampleDefinitions={draftSampleDefinitions}
              setSampleTestPlans={setDraftSampleTestPlans}
              locked={!isEditingStudyDesign}
              viewer={viewer}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  headerButtons: {
    marginRight: 10,
    // StudyDesignEditorHeaderButtons > ReviewBadge / StudyPhaseDropdown overflow at narrow widths.
    flex: 'none',
  },
  loadingDiv: {
    paddingBottom: 5,
  },
  mockContentTitle: {
    ...FONT_PROXIMA_TITLE,
    padding: 10,
    border: 'lightgray solid 1px',
  },
  mockContentTable: {
    padding: 10,
    height: 200,
    ...FONT_PROXIMA_TITLE,
    backgroundColor: GRAY_LIGHT,
  },
  toc: {
    display: 'flex',
    backgroundColor: WHITE,
    width: '13%',
    minWidth: 200,
    paddingLeft: 16,
    paddingRight: 10,
    position: 'sticky',
    top: '0',
    height: '100%',
    minHeight: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
});

export default QueryingClinicalStudyDesignTabContents;
